import { useTu } from "@src/appV2/i18n/useTu";
import { formatLongDate, formatTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Stack } from "@mui/material";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { BottomSheet } from "@src/appV2/redesign/components/BottomSheet";
import { Dialog } from "@src/appV2/redesign/components/Dialog";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { DialogHeader } from "@src/appV2/redesign/components/DialogHeader";
import { InfoBanner } from "@src/appV2/redesign/components/InfoBanner/InfoBanner";
import { Text } from "@src/appV2/redesign/components/Text";
import { TimeSlotAvatar } from "@src/appV2/redesign/Shift/TimeSlotAvatar";
import { type TimeSlot } from "@src/appV2/redesign/Shift/types";
import { type TimeSlotWithExtras } from "@src/appV2/Shifts/types";
import { type UseModalState } from "@src/lib/ui-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ApplyToOptions } from "./ApplyToOptions";
import { StartDateSelector } from "./StartDateSelector";
import { TimeSlotSelector } from "./TimeSlotSelector";
import { APPLY_TO_OPTIONS, type ApplyToOption, type EditTimeResult, type ShiftTime } from "./types";
import { buildShiftTimes } from "./utils/buildShiftTimes";
import { getDerivedTimeSlot } from "./utils/getDerivedTimeSlot";
import { getStartDateOptions } from "./utils/getStartDateOptions";
import { getTimeSlotChangeMessage } from "./utils/getTimeSlotChangeMessage";
import {
  getErrorForField,
  hasValidationErrors,
  validateShiftTimes,
} from "./utils/validateShiftTimes";

const editShiftTimeFormSchema = z.object({
  startTime: z.date(),
  endTime: z.date(),
  applyTo: z.enum([
    APPLY_TO_OPTIONS.THIS_SHIFT_ONLY,
    APPLY_TO_OPTIONS.THIS_AND_FOLLOWING,
    APPLY_TO_OPTIONS.ALL_SHIFTS,
  ]),
});

type EditShiftTimeFormValues = z.infer<typeof editShiftTimeFormSchema>;

interface EditShiftTimeDialogProps {
  viewShiftDetails?: boolean;
  modalState: UseModalState;
  initialTime: ShiftTime;
  timeSlot: TimeSlotWithExtras;
  timezone?: string;
  showApplyToOptions?: boolean;
  showAllShiftsOption?: boolean;
  qualificationName?: string;
  /**
   * When true, validates that start time is at least 2 minutes in the future.
   * Used for invites which don't support extended bookability.
   */
  validatePastStartTime?: boolean;
  /**
   * When true, validates that the start time stays within the current timeslot's boundary.
   * Use when changing the timeslot is not allowed (e.g. when posting a new shift).
   * When false (default), timeslot changes are allowed and shown as an info message.
   */
  validateTimeslotBoundary?: boolean;
  /**
   * The workplace's *static* pre-configured default times for this slot. When
   * provided alongside `validateTimeslotBoundary`, the accepted start-time range
   * is widened to include the workplace's intended hours (e.g. PM 11pm-2am).
   *
   * Must be the static slot defaults, not the shift's current time — binding the
   * widener to the mutable current time lets each save advance the accepted
   * range and walk an AM shift into PM/NOC hours (SYN-2147).
   */
  slotDefault?: ShiftTime;
  /**
   * When true, shows a date selector allowing the shift to be moved to the next day.
   * Only visible when the shift's start time is late enough (noon or later).
   * Used when editing existing shifts, not when posting or inviting.
   */
  allowDateChange?: boolean;
  /**
   * When true, changes dialog title to "Propose a new shift time"
   * and save button label to "Propose".
   */
  isProposal?: boolean;
  /**
   * When true, replaces the Cancel footer button with a "Propose" button that
   * lets an admin switch this modal into proposal mode. The header X still closes.
   */
  showProposeButton?: boolean;
  /**
   * Called when the Propose button is clicked. Only used when showProposeButton is true.
   */
  onPropose?: () => void;
  /**
   * When set, displays an error banner inside the dialog.
   * Used to show server-side validation errors (e.g. overlapping shifts).
   */
  validationError?: string;
  onSave: (result: EditTimeResult) => void | Promise<void>;
}

export function EditShiftTimeDialog(props: EditShiftTimeDialogProps) {
  const { tu } = useTu("schedule");

  const {
    viewShiftDetails,
    modalState,
    initialTime,
    timeSlot,
    timezone,
    showApplyToOptions = true,
    showAllShiftsOption = true,
    qualificationName,
    validatePastStartTime = false,
    validateTimeslotBoundary = false,
    slotDefault,
    allowDateChange = false,
    isProposal = false,
    showProposeButton = false,
    onPropose,
    validationError,
    onSave,
  } = props;

  const isMobile = useIsMobile();

  const {
    watch,
    setValue,
    reset,
    handleSubmit,
    formState: { isSubmitted },
  } = useForm<EditShiftTimeFormValues>({
    resolver: zodResolver(editShiftTimeFormSchema),
    defaultValues: {
      startTime: initialTime.start,
      endTime: initialTime.end,
      applyTo: APPLY_TO_OPTIONS.THIS_SHIFT_ONLY,
    },
    mode: "onChange",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const startTime = watch("startTime");
  const endTime = watch("endTime");
  const applyTo = watch("applyTo");

  // Use numeric timestamps instead of Date objects in the effect dependency array
  // because callers pass `new Date(...)` inline, creating a new instance every render.
  const initialStartMs = initialTime.start.getTime();

  const initialEndMs = initialTime.end.getTime();

  // The slot-boundary widener is anchored to the workplace's static slot
  // defaults, never the mutable current time, so the accepted range can't walk
  // across saves (SYN-2147). May be undefined while defaults load or for
  // callers that don't enforce the boundary.
  const slotDefaultStartMs = slotDefault?.start.getTime();
  const slotDefaultEndMs = slotDefault?.end.getTime();

  // Reset the form only on the closed -> open transition, never on a later
  // initialTime change while the modal is already open. On submit the parent
  // updates its times synchronously (ShiftPostingForm.handleSaveTime) before the
  // mutation resolves, so a fresh initialStartMs/initialEndMs flows in mid-save.
  // Resetting on that would visibly snap `applyTo` back to "Just to this shift"
  // (SYN-2295). Reading the latest initialStartMs/initialEndMs inside the guard
  // keeps each open initializing with the correct times.
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = modalState.modalIsOpen && !wasOpenRef.current;
    wasOpenRef.current = modalState.modalIsOpen;

    if (justOpened) {
      reset({
        startTime: new Date(initialStartMs),
        endTime: new Date(initialEndMs),
        applyTo: APPLY_TO_OPTIONS.THIS_SHIFT_ONLY,
      });
    }
  }, [modalState.modalIsOpen, initialStartMs, initialEndMs, reset]);

  // Orientation persists under a derived am/pm/noc `name`, so validation runs
  // against that 3-slot value rather than against "ori".
  const isOrientation = timeSlot === "ori";

  const validationSlot: TimeSlot = isOrientation
    ? getDerivedTimeSlot(startTime, timezone)
    : timeSlot;

  const validationErrors = useMemo(
    () =>
      validateShiftTimes({
        startTime,
        endTime,
        timeSlot: validationSlot,
        timezone,
        validatePastStartTime,
        validateTimeslotBoundary,
        slotDefaultStartTime: isDefined(slotDefaultStartMs)
          ? new Date(slotDefaultStartMs)
          : undefined,
        slotDefaultEndTime: isDefined(slotDefaultEndMs) ? new Date(slotDefaultEndMs) : undefined,
      }),
    [
      startTime,
      endTime,
      validationSlot,
      timezone,
      validatePastStartTime,
      validateTimeslotBoundary,
      slotDefaultStartMs,
      slotDefaultEndMs,
    ]
  );

  const derivedTimeSlot = useMemo(
    () => getDerivedTimeSlot(startTime, timezone),
    [startTime, timezone]
  );

  const timeSlotWillChange = !isOrientation && derivedTimeSlot !== timeSlot;

  const timeSlotChangeMessage =
    !validateTimeslotBoundary && timeSlotWillChange
      ? getTimeSlotChangeMessage({ timeSlot: derivedTimeSlot, isProposal, tu })
      : undefined;

  const startTimeError = isSubmitted ? getErrorForField(validationErrors, "start") : undefined;
  const endTimeError = isSubmitted ? getErrorForField(validationErrors, "end") : undefined;
  const durationError = isSubmitted ? getErrorForField(validationErrors, "duration") : undefined;

  const onSubmit = useCallback(
    async (formValues: EditShiftTimeFormValues) => {
      if (hasValidationErrors(validationErrors)) {
        return;
      }

      setIsSubmitting(true);
      try {
        await Promise.resolve(
          onSave({
            start: formValues.startTime,
            end: formValues.endTime,
            applyTo: formValues.applyTo,
            newTimeSlot: timeSlotWillChange ? derivedTimeSlot : undefined,
          })
        );
      } catch {
        // Parent rejected — stay open (validation error displayed via prop)
        return;
      } finally {
        setIsSubmitting(false);
      }

      modalState.closeModal();
    },
    [validationErrors, timeSlotWillChange, onSave, modalState, derivedTimeSlot]
  );

  const handleSave = handleSubmit(onSubmit);

  const handleCancel = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  const handleStartTimeChange = useCallback(
    (date: Date) => {
      const result = buildShiftTimes({
        startDate: date,
        startTime: date,
        endTime,
        timezone,
      });
      setValue("startTime", result.startTime);
      setValue("endTime", result.endTime);
    },
    [endTime, setValue, timezone]
  );

  const handleEndTimeChange = useCallback(
    (date: Date) => {
      const result = buildShiftTimes({
        startDate: startTime,
        startTime,
        endTime: date,
        timezone,
      });
      setValue("endTime", result.endTime);
    },
    [startTime, setValue, timezone]
  );

  const handleApplyToChange = useCallback(
    (option: ApplyToOption) => {
      setValue("applyTo", option);
    },
    [setValue]
  );

  const dateOptions = useMemo(
    () => getStartDateOptions(initialTime.start, timezone),
    [initialTime.start, timezone]
  );

  const showDateSelector = allowDateChange && dateOptions.canGoForward;

  const handleDateChange = useCallback(
    (newDate: Date) => {
      const result = buildShiftTimes({
        startDate: newDate,
        startTime,
        endTime,
        timezone,
      });
      setValue("startTime", result.startTime);
      setValue("endTime", result.endTime);
    },
    [startTime, endTime, setValue, timezone]
  );

  const formatOptions = { timeZone: timezone };
  const timeRangeText = `${formatTime(initialTime.start, formatOptions)} - ${formatTime(initialTime.end, formatOptions)}`;
  const dateText = formatLongDate(initialTime.start, formatOptions);

  const content = (
    <Stack spacing={5}>
      {validationError && (
        <InfoBanner
          variant="error"
          title={
            isProposal
              ? tu("Unable to propose this shift time")
              : tu("Unable to update this shift time")
          }
          message={validationError}
        />
      )}
      {timeSlotChangeMessage && (
        <InfoBanner
          variant="info"
          icon={
            <InfoOutlinedIcon
              fontSize="small"
              sx={(theme) => ({ color: theme.palette.primary.main })}
            />
          }
          title={timeSlotChangeMessage}
          message=""
        />
      )}

      {viewShiftDetails && (
        <Box
          sx={(theme) => ({
            backgroundColor: theme.palette.background.base,
            border: `1px solid ${theme.palette.border?.base}`,
            borderRadius: theme.borderRadius?.xLarge,
            padding: 4,
          })}
        >
          <Stack direction="row" alignItems="center" spacing={3}>
            <TimeSlotAvatar timeSlot={timeSlot} />
            <Box>
              <Text semibold variant="body2">
                {timeRangeText}
              </Text>
              <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.disabled })}>
                {dateText}
              </Text>
            </Box>
          </Stack>
        </Box>
      )}
      {showDateSelector && (
        <Box>
          <Text
            semibold
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              fontSize: theme.typography.caption.fontSize,
              marginBottom: "2px",
            })}
            variant="caption"
          >
            {tu("Start date")}
          </Text>
          <StartDateSelector
            currentDay={dateOptions.currentDay}
            nextDay={dateOptions.nextDay}
            selectedDate={startTime}
            timezone={timezone}
            onDateChange={handleDateChange}
          />
        </Box>
      )}

      <TimeSlotSelector
        endTime={endTime}
        endTimeError={endTimeError}
        startTime={startTime}
        startTimeError={startTimeError}
        timezone={timezone}
        onEndTimeChange={handleEndTimeChange}
        onStartTimeChange={handleStartTimeChange}
      />

      {durationError && (
        <Text color="error" variant="caption">
          {durationError}
        </Text>
      )}

      {showApplyToOptions && (
        <ApplyToOptions
          qualificationName={qualificationName}
          selectedOption={applyTo}
          showAllShiftsOption={showAllShiftsOption}
          timeSlot={timeSlot}
          onOptionChange={handleApplyToChange}
        />
      )}
    </Stack>
  );

  const dialogTitle = isProposal ? tu("Propose a new shift time") : tu("Edit shift time");

  const hasNoChanges =
    startTime.getTime() === initialTime.start.getTime() &&
    endTime.getTime() === initialTime.end.getTime();

  const footerContent = (
    <DialogFooter
      primaryAction={{
        label: isProposal ? tu("Propose") : tu("Save"),
        disabled: hasNoChanges || isSubmitting,
        isLoading: isSubmitting,
        onClick: handleSave,
      }}
      secondaryAction={
        showProposeButton && isDefined(onPropose)
          ? {
              label: tu("Propose"),
              disabled: isSubmitting,
              onClick: onPropose,
            }
          : {
              label: tu("Cancel"),
              onClick: handleCancel,
            }
      }
    />
  );

  if (isMobile) {
    return (
      <BottomSheet modalState={modalState}>
        <DialogHeader title={dialogTitle} onClose={handleCancel} />
        <Box
          sx={(theme) => ({
            padding: theme.spacing(5),
            overflowY: "auto",
            flex: 1,
          })}
        >
          {content}
        </Box>
        <Box
          sx={(theme) => ({
            borderTop: `1px solid ${theme.palette.border?.base}`,
          })}
        >
          {footerContent}
        </Box>
      </BottomSheet>
    );
  }

  return (
    <Dialog footerContent={footerContent} modalState={modalState} title={dialogTitle} width={440}>
      {content}
    </Dialog>
  );
}
